/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {logCurrentThemeAccess} from './DLSThemeLogger';
import {currentTheme, multiThemeEnabled} from './ThemeStore';
import {DLSThemes} from './types';

/**
 * Defines the shape of the object returned by the useThemeContext hook.
 */
export type UseThemeContextResults = {
  theme: DLSThemes;
  isCurrent: boolean;
  isLegacy: boolean;
  multiThemeEnabled: boolean;
};

/**
 * Determines if the currently selected theme is the current theme; i.e.
 * the latest DLS version.
 *
 * ```typescript
 *  import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
 *
 *  if (isCurrentTheme()) {
 *    ...
 *  }
 * ```
 *
 * @returns `true` if the current theme is `Themes.current`, otherwise
 *          `false`.
 */
export const isCurrentTheme = (): boolean => (
  getCurrentTheme() === DLSThemes.current
);

/**
 * Determines if the currently selected theme is the legacy theme; i.e.
 * the old DLS version.
 *
 * ```typescript
 *  import {isLegacyTheme} from '@ion/dls/src/theme/useThemeContext';
 *
 *  if (isLegacyTheme()) {
 *    ...
 *  }
 * ```
 *
 * @returns `true` if the current theme is `Themes.legacy`, otherwise
 *          `false`.
 */
export const isLegacyTheme = (): boolean => (
  getCurrentTheme() === DLSThemes.legacy
);

/**
 * Determines if multi-theme switching is enabled.
 *
 * ```typescript
 *  import {isMultiThemeEnabled} from '@ion/dls/src/theme/useThemeContext';
 *
 *  if (isMultiThemeEnabled()) {
 *    ...
 *  }
 * ```
 *
 * @returns `true` if multi-theme switching is enabled, otherwise `false`.
 */
export const isMultiThemeEnabled = (): boolean => (
  multiThemeEnabled.value
);

/**
 * Returns the current theme identifier imperatively.
 *
 * Useful for imperative implementations where reactive updates
 * are not needed.
 *
 * ```typescript
 * import {getCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
 *
 * const theme = getCurrentTheme();
 * console.log(theme); // Themes.legacy or Themes.current
 * ```
 *
 * @returns The current theme identifier.
 */
export const getCurrentTheme = (): DLSThemes => {
  const theme = currentTheme.value;
  logCurrentThemeAccess(theme);
  return theme;
};

/**
 * Provides access to the currently selected theme identifier and related
 * theme state information.
 *
 * Returns an object containing the current theme, and boolean flags
 * indicating whether the current or legacy theme is active. Updates
 * automatically when signal values change.
 *
 * ```typescript
 * import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
 *
 * const MyComponent = () => {
 *   const {theme, isCurrent, isLegacy} = useThemeContext();
 *   return (
 *     <div>
 *       Current theme: {theme}
 *       {isCurrent && <span>Using current theme</span>}
 *     </div>
 *   );
 * };
 * ```
 *
 * @returns An object containing theme, isCurrent, and isLegacy properties.
 */
export const useThemeContext = (): UseThemeContextResults => {
  const [theme, setCurrentTheme] = useState(currentTheme?.value);
  const [isMultiThemeEnabled, setMultiThemeEnabled] = useState(multiThemeEnabled?.value);

  useEffect(() => {
    const unsubscribeTheme = currentTheme.subscribe(setCurrentTheme);
    const unsubscribeMultiThemeEnabled = multiThemeEnabled.subscribe(setMultiThemeEnabled);
    return () => {
      unsubscribeTheme();
      unsubscribeMultiThemeEnabled();
    };
  }, []);

  return {
    multiThemeEnabled : isMultiThemeEnabled,
    isCurrent         : theme === DLSThemes.current,
    isLegacy          : theme === DLSThemes.legacy,
    theme
  };
};

export default useThemeContext;
